import { useEffect } from 'react';
import { useController } from '../state/context.js';

const TEXT_FIELDS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return TEXT_FIELDS.has(target.tagName) || target.isContentEditable;
}

function focusedThreadId(): string | undefined {
  const row = document.activeElement?.closest('[data-thread-id]');
  return row instanceof HTMLElement ? row.dataset['threadId'] : undefined;
}

function moveRowFocus(step: number): void {
  const rows = [...document.querySelectorAll<HTMLElement>('[data-row-open]')];
  if (rows.length === 0) return;
  const index = rows.indexOf(document.activeElement as HTMLElement);
  (index === -1 ? rows[0] : rows[index + step])?.focus();
}

/**
 * The keys a mail client is expected to answer to. Nothing fires while a text field or
 * the editor has focus, except the two that belong to compose itself.
 */
export function useShortcuts(): void {
  const controller = useController();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.altKey) return;
      const state = controller.state;
      const dialogOpen = document.querySelector('[role="alertdialog"], [role="dialog"]') !== null;

      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        if (state.draft === null || state.draft.to.trim() === '') return;
        event.preventDefault();
        void controller.sendDraft();
        return;
      }
      if (event.metaKey || event.ctrlKey) return;

      if (event.key === 'Escape') {
        if (state.draft === null || dialogOpen) return;
        event.preventDefault();
        controller.closeCompose();
        return;
      }

      if (isTyping(event.target) || dialogOpen || state.draft !== null) return;

      const threadId = state.activeThread?.id ?? focusedThreadId();
      const move = (role: 'archive' | 'trash'): void => {
        const folder = state.folders.find((f) => f.role === role);
        if (threadId === undefined || folder === undefined) return;
        void controller.setThreadFlags(threadId, { folderId: folder.id });
      };

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          moveRowFocus(1);
          return;
        case 'ArrowUp':
          event.preventDefault();
          moveRowFocus(-1);
          return;
        case 'c':
          event.preventDefault();
          controller.composeNew();
          return;
        case 'r':
          event.preventDefault();
          controller.replyToOpenThread(false);
          return;
        case 'a':
          event.preventDefault();
          controller.replyToOpenThread(true);
          return;
        case 'f':
          event.preventDefault();
          controller.forwardOpenThread();
          return;
        case 'e':
          event.preventDefault();
          move('archive');
          return;
        case 'Delete':
          event.preventDefault();
          move('trash');
          return;
        case 'u': {
          if (threadId === undefined) return;
          event.preventDefault();
          const thread =
            state.activeThread?.id === threadId
              ? state.activeThread
              : state.threads.find((t) => t.id === threadId);
          if (thread === undefined) return;
          void controller.setThreadFlags(threadId, { unread: !thread.unread });
          return;
        }
        default:
          return;
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [controller]);
}
